import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  ScrollText, Search, Filter, Download, ChevronLeft, ChevronRight,
  ShieldCheck, ShieldOff, KeyRound, UserPlus, UserMinus, RefreshCcw,
  HandCoins, Calendar, FileText, Receipt, AlertTriangle, Megaphone,
  Cog, LogIn, LogOut, Lock, Database, Trash2, Pencil,
} from "lucide-react";

const ACTION_ICON = {
  "user.create": UserPlus,
  "user.update": Pencil,
  "user.disable": ShieldOff,
  "user.enable": ShieldCheck,
  "user.delete": UserMinus,
  "user.role_change": Cog,
  "user.permission_change": Cog,
  "user.password_set": KeyRound,
  "user.password_clear": Lock,
  "auth.login_success_password": LogIn,
  "auth.login_success_google": LogIn,
  "auth.login_failure": AlertTriangle,
  "auth.login_lockout": Lock,
  "auth.login_unauthorized": AlertTriangle,
  "auth.logout": LogOut,
  "integration.connect": RefreshCcw,
  "integration.disconnect": RefreshCcw,
  "integration.update": Cog,
  "integration.google_reconnect": RefreshCcw,
  "integration.receipt_folder_set": FileText,
  "receipt.upload": Receipt,
  "receipt.delete": Trash2,
  "kpi.create": HandCoins,
  "kpi.update": HandCoins,
  "kpi.delete": HandCoins,
  "announcement.create": Megaphone,
  "announcement.update": Megaphone,
  "announcement.delete": Megaphone,
  "backup.run_success": Database,
  "backup.run_failure": Database,
  "backup.run_skipped": Database,
};

const ACTION_TONE = {
  // green-ish
  "user.create": "sage", "user.enable": "sage",
  "auth.login_success_password": "sage", "auth.login_success_google": "sage",
  "integration.connect": "sage", "backup.run_success": "sage",
  // red-ish
  "user.disable": "terracotta", "user.delete": "terracotta",
  "auth.login_failure": "terracotta", "auth.login_unauthorized": "terracotta",
  "auth.login_lockout": "terracotta", "integration.disconnect": "terracotta",
  "receipt.delete": "terracotta", "kpi.delete": "terracotta",
  "announcement.delete": "terracotta", "backup.run_failure": "terracotta",
  // amber/gold
  "user.password_set": "gold", "user.password_clear": "gold",
  "user.role_change": "gold", "user.permission_change": "gold",
  "integration.google_reconnect": "gold", "backup.run_skipped": "gold",
};

const ENTITY_LABELS = {
  user: "Users", auth: "Authentication", integration: "Integrations",
  receipt: "Receipts", kpi: "KPIs", announcement: "Announcements",
  backup: "Backups",
};

export function relativeTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const diff = Math.round((Date.now() - d.getTime()) / 1000);
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
    if (diff < 86400 * 30) return `${Math.round(diff / 86400)}d ago`;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch { return iso; }
}

export function toneClass(tone) {
  switch (tone) {
    case "sage": return { wash: "bg-sage/15", text: "text-sage", border: "border-sage/30" };
    case "terracotta": return { wash: "bg-terracotta/12", text: "text-terracotta", border: "border-terracotta/30" };
    case "gold": return { wash: "bg-gold/15", text: "text-gold", border: "border-gold/30" };
    default: return { wash: "bg-brand-primary/8", text: "text-brand-primary/65", border: "border-brand-primary/15" };
  }
}

function friendlyAction(a) {
  return (a || "")
    .replace(/^[a-z]+\./, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

export function AuditRow({ row, dense = false }) {
  const Icon = ACTION_ICON[row.action] || ScrollText;
  const tone = ACTION_TONE[row.action] || "neutral";
  const t = toneClass(tone);
  return (
    <div
      data-testid={`audit-row-${row.id}`}
      className={`flex items-start gap-3 ${dense ? "py-2" : "py-3"} border-b border-brand-primary/8 last:border-b-0`}
    >
      <div className={`h-8 w-8 rounded-xl shrink-0 grid place-items-center ${t.wash}`}>
        <Icon className={`h-4 w-4 ${t.text}`} strokeWidth={1.7} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-brand-primary leading-snug">{row.summary}</p>
        <div className="flex items-center gap-2 flex-wrap mt-1 text-xs text-brand-primary/55">
          <span>{relativeTime(row.at)}</span>
          {row.actor_name && (
            <>
              <span className="text-brand-primary/30">·</span>
              <span className="text-brand-primary/75">{row.actor_name}</span>
            </>
          )}
          {!dense && row.action && (
            <>
              <span className="text-brand-primary/30">·</span>
              <code className="font-mono text-[10px] text-brand-primary/55">{row.action}</code>
            </>
          )}
          {!dense && row.ip && (
            <>
              <span className="text-brand-primary/30">·</span>
              <span className="font-mono text-[10px]">{row.ip}</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/** Compact card for the Home page (admins only). */
export function RecentAdminActivityCard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/audit-log/recent");
      setRows(data.rows || []);
    } catch {
      // silent — non-admins won't have access; the parent should hide this card
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  return (
    <Card data-testid="recent-admin-activity-card" className="rounded-2xl bg-white border border-brand-primary/10 shadow-warm p-6">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <ScrollText className="h-4 w-4 text-terracotta" strokeWidth={1.8} />
          <p className="font-heading text-base font-semibold text-brand-primary">Recent admin activity</p>
        </div>
        <Link
          to="/audit-log"
          data-testid="audit-card-view-all"
          className="text-xs text-terracotta hover:underline"
        >
          View all
        </Link>
      </div>
      {loading ? (
        <p className="text-sm text-brand-primary/45 py-4 text-center">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-brand-primary/45 py-4 text-center" data-testid="audit-card-empty">
          No admin activity yet.
        </p>
      ) : (
        <div className="-my-1">
          {rows.slice(0, 6).map((r) => <AuditRow key={r.id} row={r} dense />)}
        </div>
      )}
    </Card>
  );
}

/** Full searchable / filterable page. */
export default function AuditLogPage() {
  const [filters, setFilters] = useState({
    actor_user_id: "", action: "", entity_type: "", q: "", since: "", until: "",
  });
  const [skip, setSkip] = useState(0);
  const [limit] = useState(50);
  const [data, setData] = useState({ rows: [], total: 0, entity_types: [] });
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [actions, setActions] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit, skip };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const [{ data: log }, { data: actionsData }] = await Promise.all([
        api.get("/audit-log", { params }),
        actions.length ? Promise.resolve({ data: { actions } }) : api.get("/audit-log/actions"),
      ]);
      setData(log);
      if (actionsData?.actions) setActions(actionsData.actions);
    } catch (err) {
      // 403 for non-admins — handled by route guard
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/users?include_inactive=true").then(({ data: u }) => setUsers(u || []));
  }, []);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [skip]);

  const onSearch = () => { setSkip(0); load(); };

  const onReset = () => {
    const cleared = { actor_user_id: "", action: "", entity_type: "", q: "", since: "", until: "" };
    setFilters(cleared);
    setSkip(0);
    // Refetch immediately with cleared filters (don't wait for a manual Apply)
    (async () => {
      setLoading(true);
      try {
        const { data: log } = await api.get("/audit-log", { params: { limit, skip: 0 } });
        setData(log);
      } finally {
        setLoading(false);
      }
    })();
  };

  const exportCsv = () => {
    const headers = ["at", "action", "entity_type", "entity_id", "actor_email", "actor_name", "summary", "ip"];
    const lines = [headers.join(",")];
    for (const r of data.rows) {
      lines.push(headers.map((h) => `"${(r[h] ?? "").toString().replace(/"/g, '""')}"`).join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `audit-log_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const total = data.total || 0;
  const pageStart = total === 0 ? 0 : skip + 1;
  const pageEnd = Math.min(skip + (data.rows?.length || 0), total);

  return (
    <div className="space-y-6" data-testid="audit-log-page">
      <div>
        <p className="text-[11px] uppercase tracking-[0.2em] text-terracotta font-semibold">Admin</p>
        <h2 className="font-heading text-3xl font-semibold text-brand-primary mt-1">Audit Log</h2>
        <p className="text-sm text-brand-primary/60 mt-1">
          Append-only history of every privileged action. Use filters to narrow down what you're looking for.
        </p>
      </div>

      <Card className="rounded-2xl bg-white border border-brand-primary/10 shadow-warm p-5">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3">
          <div className="md:col-span-2">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-brand-primary/40" />
              <Input
                placeholder="Search summary / email / entity id…"
                value={filters.q}
                onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                onKeyDown={(e) => e.key === "Enter" && onSearch()}
                className="pl-9 h-10"
                data-testid="audit-filter-q"
              />
            </div>
          </div>
          <Select value={filters.actor_user_id || "__all__"} onValueChange={(v) => setFilters({ ...filters, actor_user_id: v === "__all__" ? "" : v })}>
            <SelectTrigger className="h-10" data-testid="audit-filter-actor">
              <SelectValue placeholder="Any actor" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Any actor</SelectItem>
              {users.map((u) => (
                <SelectItem key={u.user_id} value={u.user_id}>{u.name} ({u.email})</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filters.action || "__all__"} onValueChange={(v) => setFilters({ ...filters, action: v === "__all__" ? "" : v })}>
            <SelectTrigger className="h-10" data-testid="audit-filter-action">
              <SelectValue placeholder="Any action" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Any action</SelectItem>
              {actions.map((a) => (
                <SelectItem key={a} value={a}>{friendlyAction(a)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filters.entity_type || "__all__"} onValueChange={(v) => setFilters({ ...filters, entity_type: v === "__all__" ? "" : v })}>
            <SelectTrigger className="h-10" data-testid="audit-filter-entity">
              <SelectValue placeholder="Any entity" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Any entity</SelectItem>
              {(data.entity_types || []).map((e) => (
                <SelectItem key={e} value={e}>{ENTITY_LABELS[e] || e}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex gap-2">
            <Button onClick={onSearch} data-testid="audit-filter-apply" className="flex-1 bg-terracotta hover:bg-terracotta-hover text-white h-10">
              <Filter className="h-4 w-4 mr-1" /> Apply
            </Button>
            <Button onClick={onReset} variant="outline" className="h-10" data-testid="audit-filter-reset">
              Reset
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-brand-primary/55">From</label>
            <Input
              type="date"
              value={filters.since}
              onChange={(e) => setFilters({ ...filters, since: e.target.value })}
              className="h-9 mt-1"
              data-testid="audit-filter-since"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-brand-primary/55">To</label>
            <Input
              type="date"
              value={filters.until}
              onChange={(e) => setFilters({ ...filters, until: e.target.value })}
              className="h-9 mt-1"
              data-testid="audit-filter-until"
            />
          </div>
          <div className="flex items-end">
            <Button
              variant="outline"
              onClick={exportCsv}
              data-testid="audit-export-csv"
              className="h-9 w-full"
              disabled={data.rows.length === 0}
            >
              <Download className="h-4 w-4 mr-1.5" /> Export CSV
            </Button>
          </div>
        </div>
      </Card>

      <Card className="rounded-2xl bg-white border border-brand-primary/10 shadow-warm overflow-hidden">
        <div className="px-5 py-3 border-b border-brand-primary/10 flex items-center justify-between text-sm">
          <span className="text-brand-primary/65">
            {loading ? "Loading…" : `Showing ${pageStart}–${pageEnd} of ${total.toLocaleString()}`}
          </span>
          <Badge className="rounded-full bg-sage/15 text-brand-primary border-0 text-[10px]">
            <ShieldCheck className="h-3 w-3 mr-1" /> Append-only
          </Badge>
        </div>
        <div className="px-5">
          {!loading && data.rows.length === 0 && (
            <p className="text-sm text-brand-primary/45 py-12 text-center" data-testid="audit-empty-state">
              No audit entries match these filters.
            </p>
          )}
          {data.rows.map((r) => <AuditRow key={r.id} row={r} />)}
        </div>
        <div className="px-5 py-3 border-t border-brand-primary/10 flex items-center justify-between">
          <Button
            variant="outline"
            size="sm"
            disabled={skip <= 0}
            onClick={() => setSkip(Math.max(0, skip - limit))}
            data-testid="audit-prev-page"
            className="h-8"
          >
            <ChevronLeft className="h-4 w-4 mr-1" /> Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={skip + limit >= total}
            onClick={() => setSkip(skip + limit)}
            data-testid="audit-next-page"
            className="h-8"
          >
            Next <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </Card>
    </div>
  );
}
